"use client";

import { projectToLine } from "@/lib/invoice/billing";

import Link from "next/link";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useInvoiceStore } from "@/lib/invoice/store";
import {
  COPY_LABELS,
  DOC_TYPES,
  STATUS_META,
  computeTotals,
  counterKey,
  downloadBlob,
  financialYear,
  fmtDate,
  fmtMoney,
  invoiceStatus,
  lastDayOfMonth,
  monthLabel,
  num,
  sortLines,
  suggestNumber,
  toCSV,
  todayISO,
  uid,
} from "@/lib/invoice/utils";
import { clientDefaults } from "@/components/Invoice/InvoiceEditor";
import { TagList } from "@/components/Invoice/Tags";
import { invoiceTags } from "@/lib/invoice/tags";
import { buildMerged, combinedNumber, mergeProblems } from "@/lib/invoice/merge";
import { saleAmount, saleToLine, unbilledSales } from "@/lib/invoice/sales";
import { Button, Empty, Field, Input, Loading, Modal, MonthTag, PageHeader, Select, StatusBadge, cx, useFeedback } from "@/components/Invoice/ui";

// The month an invoice is for: its billing period, else the month of its date.
const monthOf = (inv) => inv.periodMonth || (inv.date || "").slice(0, 7);

function prevMonth() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function InvoicesInner() {
  const store = useInvoiceStore();
  const params = useSearchParams();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(params.get("status") || "");
  const [clientId, setClientId] = useState(params.get("client") || "");
  const [docType, setDocType] = useState("");
  const [tag, setTag] = useState(params.get("tag") || "");
  const [merge, setMerge] = useState(null);
  const [range, setRange] = useState("all");
  const [sort, setSort] = useState({ key: "date", dir: -1 });
  const [selected, setSelected] = useState(new Set());
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const { toast, confirm } = useFeedback();

  useEffect(() => {
    if (store.ready && params.get("run")) openRun();
  }, [store.ready]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(
    () =>
      store.invoices.map((inv) => {
        const t = computeTotals(inv);
        return { inv, t, status: invoiceStatus(inv, t) };
      }),
    [store.invoices]
  );

  const fys = useMemo(() => [...new Set(store.invoices.map((i) => financialYear(i.date)))].sort().reverse(), [store.invoices]);
  const months = useMemo(() => [...new Set(store.invoices.map(monthOf))].filter(Boolean).sort().reverse(), [store.invoices]);

  if (!store.ready) return <Loading />;

  const filtered = rows
    .filter(({ inv, status: s }) => {
      if (status && s !== status) return false;
      if (clientId && inv.clientId !== clientId) return false;
      if (docType && (inv.docType || "invoice") !== docType) return false;
      if (tag && !invoiceTags(inv).includes(tag)) return false;
      if (range.startsWith("fy:") && financialYear(inv.date) !== range.slice(3)) return false;
      if (range.startsWith("m:") && monthOf(inv) !== range.slice(2)) return false;
      const hay = [inv.number, inv.billTo?.name, inv.period, inv.reference, ...(inv.items || []).map((l) => l.description)].join(" ").toLowerCase();
      return hay.includes(q.toLowerCase());
    })
    .sort((a, b) => {
      const k = sort.key;
      const val = (r) => (k === "total" ? r.t.total : k === "balance" ? r.t.balance : k === "client" ? r.inv.billTo?.name || "" : k === "month" ? `${monthOf(r.inv)}|${r.inv.date || ""}` : r.inv[k] || "");
      const va = val(a);
      const vb = val(b);
      return (va > vb ? 1 : va < vb ? -1 : 0) * sort.dir || (a.inv.number > b.inv.number ? -1 : 1);
    });

  const totalsByCur = {};
  for (const { inv, t, status: s } of filtered) {
    if (s === "cancelled" || (inv.docType || "invoice") !== "invoice") continue;
    const e = (totalsByCur[inv.currency] ||= { total: 0, due: 0 });
    e.total += t.total;
    e.due += Math.max(0, t.balance);
  }

  const chosen = filtered.filter((r) => selected.has(r.inv.id));
  const exportSet = chosen.length ? chosen : filtered;

  const Th = ({ k, children, right }) => (
    <th
      className={cx("cursor-pointer select-none px-4 py-3", right && "text-right")}
      onClick={() => setSort((s) => ({ key: k, dir: s.key === k ? -s.dir : -1 }))}
    >
      {children}
      {sort.key === k ? (sort.dir > 0 ? " ▲" : " ▼") : ""}
    </th>
  );

  const exportExcel = async () => {
    setBusy(true);
    try {
      const { downloadRegisterExcel } = await import("@/lib/invoice/excel");
      await downloadRegisterExcel(exportSet.map((r) => r.inv), `invoice-register-${todayISO()}.xlsx`);
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const data = [
      ["Number", "Type", "Date", "Due", "Client", "Client GSTIN", "Period", "Currency", "Taxable", "CGST", "SGST", "IGST", "Discount", "Credit", "Total", "Paid", "Balance", "Status"],
      ...exportSet.map(({ inv, t, status: s }) => [
        inv.number, inv.docType, inv.date, inv.dueDate, inv.billTo?.name, inv.billTo?.gstin, inv.period, inv.currency,
        t.subtotal, t.cgst, t.sgst, t.igst, t.discount, t.credit, t.total, t.paid, t.balance, s,
      ]),
    ];
    downloadBlob(new Blob([toCSV(data)], { type: "text/csv" }), `invoices-${todayISO()}.csv`);
  };

  const downloadPdfs = async () => {
    if (exportSet.length > 10 && !(await confirm(`Download ${exportSet.length} PDFs one after another?`, { okLabel: "Download" }))) return;
    setBusy(true);
    try {
      const { downloadInvoicePdf } = await import("@/lib/invoice/pdf");
      for (const { inv } of exportSet) {
        await downloadInvoicePdf(inv, store);
        await new Promise((r) => setTimeout(r, 300));
      }
    } finally {
      setBusy(false);
    }
  };

  // ---------- monthly billing run ----------
  const openRun = () => {
    const month = prevMonth();
    setRun({ month, date: lastDayOfMonth(month), picked: null });
  };
  const runCandidates = run
    ? store.clients
        .filter((c) => !c.archived)
        .map((c) => {
          const basis = store.settings.defaults.prorate || "30";
          // skip projects that haven't started yet (or had ended) in the billing month
          const projects = store.projects.filter((p) => p.clientId === c.id && p.status === "active" && p.billing === "monthly" && projectToLine(p, run.month, { basis }));
          const sales = run.includeSales === false ? [] : unbilledSales(store.sales, { clientId: c.id, upto: lastDayOfMonth(run.month) });
          const existing = store.invoices.find((i) => i.clientId === c.id && i.periodMonth === run.month && i.status !== "cancelled" && (i.docType || "invoice") === "invoice");
          const retainer = projects.reduce((s, p) => {
            const l = projectToLine(p, run.month, { basis });
            return s + num(l.rate) * (num(l.qty) || 1);
          }, 0);
          const salesTotal = sales.reduce((s, x) => s + saleAmount(x), 0);
          return { c, projects: existing ? [] : projects, sales, existing, total: (existing ? 0 : retainer) + salesTotal, salesTotal };
        })
        .filter((x) => x.projects.length || x.sales.length)
    : [];
  const picked = run?.picked ?? new Set(runCandidates.map((x) => x.c.id));

  const createRun = () => {
    const d = store.settings.defaults;
    let pool = [...store.invoices];
    const created = [];
    const counters = {};
    for (const { c, projects, sales } of runCandidates.filter((x) => picked.has(x.c.id))) {
      const defs = clientDefaults(c, store, run.date);
      const brand = store.brandById(defs.brandId);
      const numbering = { ...store.settings.numbering, counters: { ...store.settings.numbering.counters, ...counters } };
      const s = suggestNumber({ ...store.settings, numbering }, pool, "invoice", run.date, brand);
      const inv = {
        id: uid(),
        docType: "invoice",
        number: s.number,
        status: "draft",
        date: run.date,
        ...defs,
        template: "",
        periodMonth: run.month,
        period: monthLabel(run.month),
        items: (() => {
          const lines = projects
            .map((p) => projectToLine(p, run.month, { basis: d.prorate || "30" }))
            .filter(Boolean)
            .concat(sales.map((x) => saleToLine(x, store.projects, d.taxRate)));
          return sortLines(lines);
        })(),
        discount: 0,
        credit: 0,
        creditLabel: "Credit Amount",
        notes: d.notes,
        terms: d.terms,
        copyLabel: COPY_LABELS[0],
        showHsn: !!d.showHsn,
        showClientLogo: d.showClientLogo,
        reference: "",
        fxRate: "",
        payments: [],
      };
      counters[counterKey(store.settings, "invoice", run.date, brand)] = s.seq + 1;
      pool = [inv, ...pool];
      created.push(inv);
    }
    if (!created.length) return toast("Nothing selected.", { tone: "error" });
    store.saveInvoices(created, counters);
    setRun(null);
    setStatus("draft");
    toast(`Created ${created.length} draft invoice(s) for ${monthLabel(run.month)} – review and finalise them.`, { tone: "success" });
  };

  const counts = {};
  for (const r of rows) counts[r.status] = (counts[r.status] || 0) + 1;

  return (
    <div>
      <PageHeader
        title="Invoices"
        subtitle={Object.entries(totalsByCur).map(([c, v]) => `${fmtMoney(v.total, c)} billed · ${fmtMoney(v.due, c)} outstanding`).join("  |  ") || "No invoices yet"}
        actions={
          <>
            <Button variant="secondary" onClick={openRun}>Monthly billing run</Button>
            <Link href="/invoice/invoices/edit">
              <Button>+ New invoice</Button>
            </Link>
          </>
        }
      />

      <div className="mb-3 flex flex-wrap gap-2">
        <Input className="max-w-xs" placeholder="Search number, client, item…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Select className="max-w-[150px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Any status</option>
          {Object.entries(STATUS_META).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </Select>
        <Select className="max-w-[220px]" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">All clients</option>
          {store.clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Select>
        <Select className="max-w-[170px]" value={tag} onChange={(e) => setTag(e.target.value)}>
          <option value="">All tags</option>
          {store.settings.tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
        <Select className="max-w-[170px]" value={docType} onChange={(e) => setDocType(e.target.value)}>
          <option value="">All documents</option>
          {Object.entries(DOC_TYPES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </Select>
        <Select className="max-w-[170px]" value={range} onChange={(e) => setRange(e.target.value)}>
          <option value="all">All dates</option>
          {fys.map((f) => (
            <option key={f} value={`fy:${f}`}>FY {f}</option>
          ))}
          {months.map((m) => (
            <option key={m} value={`m:${m}`}>{monthLabel(m)}</option>
          ))}
        </Select>
      </div>

      <div className="mb-3 flex flex-wrap gap-1.5">
        {["", "draft", "sent", "overdue", "partial", "paid"].map((k) => (
          <button
            key={k || "all"}
            onClick={() => setStatus(k)}
            className={cx(
              "rounded-full border px-3 py-1 text-xs font-medium",
              status === k ? "border-brand-700 bg-brand-700 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
            )}
          >
            {k ? STATUS_META[k].label : "All"} {k ? counts[k] || 0 : rows.length}
          </button>
        ))}
      </div>

      {months.length > 1 && (
        <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="month-chips">
          <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Month</span>
          {["all", ...months.slice(0, 12).map((m) => `m:${m}`)].map((k) => {
            const on = range === k;
            const m = k.slice(2);
            return (
              <button
                key={k}
                onClick={() => setRange(on && k !== "all" ? "all" : k)}
                className={cx("rounded-full border px-2.5 py-1 text-xs font-medium transition", on ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300")}
              >
                {k === "all" ? "All months" : new Date(+m.slice(0, 4), +m.slice(5, 7) - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "2-digit" })}
                {k !== "all" && <span className={cx("ml-1", on ? "text-white/70" : "text-slate-400")}>{rows.filter((r) => monthOf(r.inv) === m).length}</span>}
              </button>
            );
          })}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-500">
          {selected.size ? `${chosen.length} selected` : `${filtered.length} shown`} — export:
        </span>
        <Button size="sm" variant="secondary" disabled={busy || !exportSet.length} onClick={exportExcel}>Excel register</Button>
        <Button size="sm" variant="secondary" disabled={busy || !exportSet.length} onClick={exportCsv}>CSV</Button>
        <Button size="sm" variant="secondary" disabled={busy || !exportSet.length} onClick={downloadPdfs}>PDFs</Button>
        {selected.size >= 2 && (
          <Button size="sm" onClick={() => setMerge({ keep: "combined", prefix: "period", combine: false })}>
            Merge {selected.size} invoices
          </Button>
        )}
        {selected.size > 0 && <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear selection</Button>}
      </div>

      {filtered.length === 0 ? (
        <Empty title="No invoices">
          Create one with “New invoice”, or use “Monthly billing run” to bill all active retainers at once.
        </Empty>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200/80 bg-white shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={filtered.every((r) => selected.has(r.inv.id))}
                    onChange={(e) => setSelected(e.target.checked ? new Set(filtered.map((r) => r.inv.id)) : new Set())}
                  />
                </th>
                <Th k="number">Number</Th>
                <Th k="client">Client</Th>
                <Th k="month">Month</Th>
                <Th k="date">Date</Th>
                <Th k="dueDate">Due</Th>
                <Th k="total" right>Total</Th>
                <Th k="balance" right>Balance</Th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(({ inv, t, status: s }) => (
                <tr key={inv.id} className="transition-colors hover:bg-slate-50/70">
                  <td className="px-4 py-2.5">
                    <input
                      type="checkbox"
                      checked={selected.has(inv.id)}
                      onChange={() =>
                        setSelected((cur) => {
                          const n = new Set(cur);
                          n.has(inv.id) ? n.delete(inv.id) : n.add(inv.id);
                          return n;
                        })
                      }
                    />
                  </td>
                  <td className="px-4 py-2.5">
                    <Link className="font-semibold text-brand-700 hover:underline" href={`/invoice/invoices/view?id=${inv.id}`}>
                      {inv.number}
                    </Link>
                    {(inv.docType || "invoice") !== "invoice" && <div className="text-[11px] text-slate-500">{DOC_TYPES[inv.docType].label}</div>}
                  </td>
                  <td className="px-4 py-2.5">
                    <div>{inv.billTo?.name}</div>
                    {inv.period && <div className="text-xs text-slate-500">{inv.period}</div>}
                    {invoiceTags(inv).length > 0 && (
                      <div className="mt-0.5">
                        <TagList ids={invoiceTags(inv)} tags={store.settings.tags} small />
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <MonthTag date={monthOf(inv)} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{inv.date ? fmtDate(inv.date, store.settings.defaults.dateStyle) : "—"}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{inv.dueDate ? fmtDate(inv.dueDate, store.settings.defaults.dateStyle) : "—"}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right font-medium">{fmtMoney(t.total, inv.currency)}</td>
                  <td className={cx("whitespace-nowrap px-4 py-2.5 text-right", t.balance > 0 && s !== "cancelled" ? "text-brand-700" : "text-slate-400")}>
                    {(inv.docType || "invoice") === "invoice" ? fmtMoney(t.balance, inv.currency) : "—"}
                  </td>
                  <td className="px-4 py-2.5"><StatusBadge status={s} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <MergeDialog state={merge} setState={setMerge} invoices={chosen.map((r) => r.inv)} store={store} onDone={() => setSelected(new Set())} />

      <Modal
        open={!!run}
        onClose={() => setRun(null)}
        title="Monthly billing run"
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setRun(null)}>Cancel</Button>
            <Button onClick={createRun}>Create {picked.size} draft invoice(s)</Button>
          </>
        }
      >
        {run && (
          <div className="space-y-4">
            <div className="text-sm text-slate-600">
              Creates one draft invoice per client with its <b>active monthly retainers</b> plus every <b>unbilled sale</b> recorded up to the end of the month.
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={run.includeSales !== false} onChange={(e) => setRun({ ...run, includeSales: e.target.checked, picked: null })} />
              Include unbilled sales from the Sales log
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Billing month">
                <Input type="month" value={run.month} onChange={(e) => setRun({ ...run, month: e.target.value, date: lastDayOfMonth(e.target.value), picked: null })} />
              </Field>
              <Field label="Invoice date">
                <Input type="date" value={run.date} onChange={(e) => setRun({ ...run, date: e.target.value })} />
              </Field>
            </div>
            {runCandidates.length === 0 ? (
              <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No clients have active monthly projects.</div>
            ) : (
              <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {runCandidates.map(({ c, projects, sales, existing, total, salesTotal }) => (
                  <label key={c.id} className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={picked.has(c.id)}
                      onChange={() => {
                        const n = new Set(picked);
                        n.has(c.id) ? n.delete(c.id) : n.add(c.id);
                        setRun({ ...run, picked: n });
                      }}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{c.name}</div>
                      <div className="text-xs text-slate-500">
                        {projects.length > 0 && `${projects.length} retainer project${projects.length > 1 ? "s" : ""}`}
                        {projects.length > 0 && sales.length > 0 && " + "}
                        {sales.length > 0 && <span className="font-medium text-brand-700">{sales.length} unbilled sale{sales.length > 1 ? "s" : ""} ({fmtMoney(salesTotal, c.currency || store.settings.defaults.currency)})</span>}
                        {existing && <span className="ml-2 text-amber-700">Retainers already billed on {existing.number} – only new sales included</span>}
                      </div>
                    </div>
                    <div className="font-semibold">{fmtMoney(total, c.currency || store.settings.defaults.currency)}</div>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default function InvoicesPage() {
  return (
    <Suspense fallback={<Loading />}>
      <InvoicesInner />
    </Suspense>
  );
}

function MergeDialog({ state, setState, invoices, store, onDone }) {
  const { toast } = useFeedback();
  if (!state) return null;
  const problem = mergeProblems(invoices);
  const first = [...invoices].sort((a, b) => (a.date || "").localeCompare(b.date || ""))[0];
  const brand = first ? store.brandById(first.brandId) : null;
  const nextNo = first ? suggestNumber(store.settings, store.invoices, first.docType || "invoice", todayISO(), brand) : null;
  const joined = combinedNumber(invoices.map((i) => i.number));
  const newNo = state.keep === "combined" ? { number: joined, seq: null } : nextNo;
  const preview = !problem ? buildMerged(invoices, { ...state, newNumber: newNo.number }) : null;
  const total = preview ? computeTotals(preview.merged).total : 0;

  const doMerge = () => {
    const { merged, sources } = buildMerged(invoices, { ...state, newNumber: newNo.number });
    const rec = store.saveInvoice(merged, {
      seqUsed: state.keep === "new" ? nextNo.seq : null,
      action: `Merged from ${merged.mergedFrom.join(", ")}`,
    });
    for (const src of sources) {
      if (src.status === "draft") store.deleteInvoice(src.id);
      else store.saveInvoice({ ...src, status: "cancelled", mergedInto: rec.number }, { action: `Cancelled – merged into ${rec.number}` });
    }
    setState(null);
    onDone();
    toast(`Merged into ${rec.number}`, { tone: "success" });
  };

  return (
    <Modal
      open
      onClose={() => setState(null)}
      title={`Merge ${invoices.length} invoices into one`}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={() => setState(null)}>Cancel</Button>
          <Button onClick={doMerge} disabled={!!problem}>Merge</Button>
        </>
      }
    >
      {problem ? (
        <div className="rounded-lg bg-brand-50 px-4 py-3 text-sm text-brand-700">{problem}</div>
      ) : (
        <div className="space-y-4 text-sm">
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            {invoices.map((i) => (
              <div key={i.id} className="flex justify-between">
                <span>{i.number} · {i.period || i.date} · {i.items.length} lines</span>
                <span>{fmtMoney(computeTotals(i).total, i.currency)}</span>
              </div>
            ))}
            <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 font-semibold">
              <span>Merged invoice · {preview.merged.items.length} lines</span>
              <span>{fmtMoney(total, first.currency)}</span>
            </div>
          </div>
          <Field label="Invoice number">
            <Select value={state.keep} onChange={(e) => setState({ ...state, keep: e.target.value })}>
              <option value="combined">Combined number {joined} (names every merged invoice)</option>
              <option value="earliest">Keep {first.number} (earliest)</option>
              <option value="new">Next new number {nextNo.number}</option>
            </Select>
          </Field>
          <Field label="Line descriptions">
            <Select value={state.prefix} onChange={(e) => setState({ ...state, prefix: e.target.value })}>
              <option value="period">Prefix with billing period (e.g. “August 2026 – …”)</option>
              <option value="number">Prefix with original invoice number</option>
              <option value="none">Keep as they are</option>
            </Select>
          </Field>
          {state.prefix === "none" && (
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={state.combine} onChange={(e) => setState({ ...state, combine: e.target.checked })} />
              Combine identical lines (same description and rate → quantities added)
            </label>
          )}
          <div className="text-xs text-slate-500">
            The other invoices are {invoices.some((i) => i.status !== "draft") ? "marked Cancelled (“merged into …”) so your numbering stays gap-free" : "moved to the recycle bin"}
            {invoices.some((i) => i.status !== "draft") && "; draft invoices go to the recycle bin"}. Discounts and credits are added together.
          </div>
        </div>
      )}
    </Modal>
  );
}
